/** Semantische Farben: geben den Primitives kontextuelle Bedeutung. Siehe styles/CLAUDE.md. */

import {
  teal,
  green,
  red,
  orange,
  yellow,
  blue,
  gray,
  petrol,
  alpha,
} from "./primitives";

export const colors = {
  brand: {
    primary: teal[500],
    primaryLight: teal[100],
    primaryLighter: teal[50],
    primaryDark: teal[700],
    primaryDarker: teal[900],
    // Vollständige Teal-Skala für UI-Elemente
    teal50: teal[50],
    teal100: teal[100],
    teal200: teal[200],
    teal300: teal[300],
    teal400: teal[400],
    teal500: teal[500],
    teal600: teal[600],
    teal700: teal[700],
    teal800: teal[800],
    teal900: teal[900],
    // Logo-Farben (strømcraft Brand)
    logoText: teal[850],
    logoAccent: teal[650],
    // Secondary
    secondary: red[700],
    secondaryLight: red[200],
  },

  // ===== SCORE-GEWICHTUNG (Kennzahlen-Balken, z.B. Wirtschaftlichkeits-/Reifegrad-Score) =====
  // Gleiche helle Pastelltöne wie bei den Ertragsprognose-Charts (chart.p50/p75/p90),
  // generisch nach Position (Slot 1/2/3), nicht nach Kennzahlname
  scoreWeighting: [
    yellow[100],
    red[100],
    blue[100],
    green[100],
    teal[100],
    orange[100],
  ],

  // ===== ACTIVE STATE (für Badges, Toggles) =====
  active: {
    main: teal[600], // "Aktiv" Badge
    light: teal[100], // Heller Hintergrund
    background: teal[50], // Sehr heller Hintergrund
    dark: teal[700], // Dunklere Variante
  },

  // ===== TABLE ACTIONS (Icon-Farben für Tabellen-Aktionen) =====
  tableActions: {
    // Löschen (DeleteIcon)
    delete: {
      main: red[400], // Helles Rot im Normalzustand
      hover: red[400], // Kräftiges Rot beim Hover
      hoverText: gray[0], // Weiß für Text/Icon beim Hover
    },
    // Speichern (CheckIcon)
    save: {
      main: green[600], // Grün im Normalzustand
      hover: alpha.green08, // Heller Hintergrund beim Hover
    },
    // Abbrechen (CloseIcon)
    cancel: {
      main: gray[600], // Grau im Normalzustand
      hover: alpha.black04, // Dezenter Hintergrund beim Hover
    },
    // Auszahlungsplan (PaymentsIcon)
    payments: {
      main: teal[400], // Teal im Normalzustand
      hover: teal[500], // Kräftiges Teal beim Hover
      hoverText: gray[0], // Weiß für Icon beim Hover
    },
    // Hauptdarlehen markieren (StarIcon)
    highlight: {
      main: teal[400], // Teal wenn aktiv
      hover: alpha.yellow08, // Heller Hintergrund beim Hover
      inactive: gray[400], // Grau wenn inaktiv
    },
    // Zeile erweitern (ExpandIcon)
    expand: {
      active: teal[400], // Primary wenn aktiv
      inactive: gray[600], // Grau wenn inaktiv
    },
  },

  // ===== BUTTONS (Custom Button Component) =====
  button: {
    // Primary Button (gefüllt)
    primary: {
      background: teal[500], // Hintergrund
      text: gray[0], // Text (weiß)
      hover: teal[700], // Hover-Hintergrund
      shadow: teal[200], // Schatten
      shadowHover: teal[300], // Schatten beim Hover
    },
    // Secondary Button (outlined)
    secondary: {
      text: teal[500], // Text & Border
      border: teal[500], // Border-Farbe
      textHover: teal[700], // Text beim Hover
      hover: teal[100], // Hintergrund beim Hover
      active: teal[50], // Hintergrund bei Active
    },
  },

  // ===== STATUS (Feedback-Farben) =====
  status: {
    success: {
      main: green[600],
      light: green[50],
      background: green[50],
    },
    warning: {
      main: orange[600],
      accent: orange[400],
      light: orange[50],
      background: orange[50],
      tint: alpha.yellowTint20,
    },
    error: {
      main: red[500],
      light: red[50],
      background: red[50],
      tint: alpha.redTint15,
    },
    info: {
      main: blue[500],
      light: blue[50],
      background: blue[50],
    },
  },

  // ===== REIFEGRAD-SEGMENTE (Ampelfarben für Domänen-Breakdown, freundliches Pastell statt kräftiger Statusfarben) =====
  reifegradSegment: {
    success: green[200],
    warning: orange[200],
    error: red[200],
    missing: gray[300],
  },

  // ===== PAGE HEADER (dark head of every project page: title, stage, tabs) =====
  pageHeader: {
    gradientStart: petrol[900],
    gradientMiddle: petrol[800],
    gradientEnd: petrol[600],
    glow: alpha.petrolSpot60,
    text: gray[0],
    textSecondary: petrol[300],
    textMuted: alpha.white70,
    line: alpha.white18,
    lineDone: petrol[400],
    dotBorder: alpha.white35,
    dotDone: petrol[400],
    dotCurrentRing: alpha.petrolGlow35,
    accent: petrol[500],
    accentLight: petrol[100],
    buttonShadow: alpha.black30,
    tabsBackground: gray[0],
    tabsShadow: alpha.petrolDark18,
    tabActiveShadow: alpha.petrolShadow40,
  },

  // ===== ZEITSTRAHL (dünne Linie in einer Farbe, die Dringlichkeit tragen die Punkte) =====
  zeitstrahl: {
    strahl: gray[250],
  },

  // ===== DOMÄNEN-FORTSCHRITT (einheitliche Farbe je Fortschrittsstufe, unabhängig von der Domäne) =====
  domainProgress: {
    notStarted: { background: gray[150], main: gray[600] },
    inProgress: { background: blue[50], main: blue[600] },
    completed: { background: green[50], main: green[600] },
    risk: { background: red[50], main: red[600] },
  },

  // ===== KPI-SKALA (Bewertungsfarben für DSCR, LLCR, IRR) =====
  kpi: {
    noData: gray[400], // Keine Daten
    gradientStart: red[100], // KPI-Verlauf Start (Rot)
    gradientEnd: green[100], // KPI-Verlauf Ende (Grün)
    gradientTextStart: red[300], // KPI-Textverlauf Start (dunkler)
    gradientTextEnd: green[300], // KPI-Textverlauf Ende (dunkler)
    critical: red[500], // < 1.00 - nicht tragfähig (kräftiges Rot)
    poor: red[400], // 1.00-1.05 - inakzeptabel (mittleres Rot)
    warning: orange[300], // 1.05-1.10 - sehr kritisch (lebhaftes Orange)
    minimum: yellow[300], // 1.10-1.15 - Mindestniveau (lebhaftes Gelb)
    acceptable: green[300], // 1.15-1.20 - akzeptabel (helles Grün)
    good: green[400], // 1.20-1.30 - gut/bankfähig (mittleres Grün)
    excellent: green[600], // > 1.30 - sehr gut (sattes Dunkelgrün)
  },

  // ===== WIRTSCHAFTLICHKEITS-SCORE (Verlauf hell-rot → dunkel-grün) =====
  scoreGradient: {
    wirtschaftlichkeit: {
      backgroundLow: red[100], // Schlecht - helles Rot
      backgroundMid: orange[200], // Mittel - Orange
      backgroundHigh: green[300], // Gut - kräftigeres Grün
      textLow: red[500], // Schlecht - Rot
      textMid: orange[700], // Mittel - dunkles Orange
      textHigh: green[900], // Gut - Dunkelgrün
    },
  },

  // ===== ÜBERSICHTSKARTE (Portfolio-Kartenanzeige) =====
  overviewMap: {
    polygonFill: teal[200], // Halbtransparentes Projektgebiet (Hover)
    polygonBorder: teal[600], // Umriss des Projektgebiets (Hover)
  },

  // ===== KARTE (Map-Features) =====
  map: {
    plannedWindTurbines: blue[300], // Windenergieanlagen (helles Blau)
    existingWindParks: gray[500], // Bestehende Parks
    protectedAreas: green[800], // Schutzgebiete
    energyInfrastructure: red[400], // Infrastruktur
    landParcels: orange[500], // Flurstücke
    settlements: blue[400], // Siedlungen
    projectArea: "#124E78", // Projektfläche (Dunkelblau)
    focusOverlay: gray[100], // Dimm-Overlay außerhalb der Projektfläche
  },

  chart: {
    // P-Value Farben (basierend auf Parcel-Farbpalette)
    bruttoertrag: orange[100],
    p50: yellow[100],
    p75: green[100],
    p90: blue[100],
    selected: orange[200],

    // Allgemeine Chart-Farben
    primary: orange[100], // Pastellorange (ersetzt Cyan)
    text: gray[750],
    border: gray[300],
    stroke: gray[200],
    cashflow: blue[300], // Sky-Blau für Cashflow-Linien

    // Tarif-Erlös Farben
    tariff: {
      eeg: green[100], // EEG-Vergütung (wie P75)
      marktpreis: blue[100], // Marktpreis (wie P50)
      // PPA-Farbpalette (sanfte Pastellfarben)
      ppa: [
        yellow[100], // Sanftes Gelb
        blue[100], // Sanftes Blau (ersetzt Lavendel)
        green[200], // Sanftes Limette
        green[100], // Sanftes Grün (ersetzt Rosa)
        blue[200], // Sanftes Cyan
        orange[100], // Sanftes Pfirsich/Orange
        red[100], // Sanftes Koralle
      ],
    },

    // Flurstück-Daten in Charts (gleiche Farbe wie Map!)
    parcels: orange[500],
  },

  // ===== PARCEL STATUS (Flurstück-Verhandlungsstatus) =====
  parcelStatus: {
    keinKontakt: gray[200], // Weißgrau - noch nicht kontaktiert
    kontaktiert: yellow[100], // Pastellgelb - in Verhandlung
    abgelehnt: red[100], // Pastellrot - Verhandlung gescheitert
    abgeschlossen: green[100], // Pastellgrün - Vertrag abgeschlossen
  },

  // ===== TURBINE STATUS (Farbe nach schlimmstem Flurstück-Status) =====
  turbineStatus: {
    keinKontakt: gray[400], // Medium-Grau (sichtbar auf Map)
    kontaktiert: yellow[300], // Medium-Gelb
    abgelehnt: red[300], // Medium-Rot
    abgeschlossen: green[300], // Medium-Grün
  },

  background: {
    default: gray[50],
    paper: gray[0],
    card: gray[0],
    tableHeader: gray[100],
    tableHeaderAlt: gray[150], // Leicht getönter Header
    tableHeaderPreOp: gray[175], // Pre-Betriebsjahr Header (bläulich-grau)
    tableRow: gray[100],
  },

  text: {
    primary: gray[700],
    secondary: gray[600],
    disabled: gray[400],
    label: gray[700],
    onDark: gray[0],
  },

  border: {
    default: alpha.black23, // Standard MUI Border (Input-Felder)
    light: alpha.black12,
    medium: alpha.black23,
    dark: gray[950],
    focus: blue[500],
    error: red[500],
    divider: gray[200],
  },

  shadow: {
    light: alpha.black08,
    medium: alpha.black10,
    heavy: alpha.black15,
    card: gray[900],
    cardHover: gray[850],
    // Focus/Error Schatten für Input-Felder
    focus: alpha.blue15,
    error: alpha.red12,
    // Deprecated Aliase für alte Struktur
    primary: blue[300],
    primaryHover: blue[400],
    default: gray[900],
    defaultHover: gray[850],
  },

  tooltip: {
    background: gray[0], // Clean-Weiß
    border: alpha.black12,
    heading: gray[700], // Überschrift
    text: gray[600], // Fließtext
  },

  glass: {
    background: alpha.white15, // Standard Glas-Hintergrund
    backgroundSubtle: alpha.white08, // Dezenter Glas-Hintergrund
    backgroundLayer: alpha.white12, // LayerControl Hintergrund
    backgroundMedium: alpha.white35, // Mittlerer Glas-Hintergrund (Overlays)
    backgroundStrong: alpha.white95, // Fast opaker Glas-Hintergrund
    backgroundSolid: alpha.white90, // Solider Glas-Hintergrund (Maps, Controls)
    backgroundSemiSolid: alpha.white80, // Semi-solider Hintergrund
    border: alpha.white10, // Glas-Border
    borderLayer: alpha.white18, // LayerControl Border
    borderStrong: alpha.white30, // Stärkerer Glas-Border
    borderSubtle: alpha.white20, // Dezenter Glas-Border
    inset: alpha.white25, // Inset-Highlight für 3D-Effekt
    insetSubtle: alpha.white10, // Dezenter Inset-Highlight
  },

  icon: {
    subtle: alpha.black15, // Dezente Icons (z.B. Delete-Button im Ruhezustand)
    default: gray[600], // Standard-Icon-Farbe
  },

  state: {
    // Hover-Zustände
    hover: alpha.black04,
    hoverMedium: alpha.black08,
    hoverPrimary: alpha.blue04,
    hoverPrimarySubtle: alpha.blue05,
    hoverPrimaryMedium: alpha.blue08,
    hoverSuccess: alpha.green08,
    hoverError: alpha.red08,
    hoverErrorMedium: alpha.red12,

    // Spezielle States
    disabled: alpha.black06,
    disabledLayer: alpha.gray50, // LayerControl disabled
    disabledText: alpha.black38,
    selected: alpha.blue08,

    // Highlight (Star-Icon, Warning-Badges)
    highlight: yellow[500],
    hoverHighlight: alpha.yellow08,
    highlightDark: yellow[600],
    highlightBackground: alpha.yellow08,
  },

  gradient: {
    primary: `linear-gradient(45deg, ${blue[500]}, ${blue[300]})`,
    primaryHover: `linear-gradient(45deg, ${blue[600]}, ${blue[500]})`,
  },

  // ===== HINWEIS „IN ENTWICKLUNG" (Absperrband über einem Modul, das noch im Bau ist) =====
  // Eigene Gruppe statt geliehener Tokens: Die Warnfarben gehören zum Zustand einer Angabe,
  // hier geht es um den Zustand der Anwendung selbst. Gedeckte Ocker statt Signalgelb – das
  // Band soll den Bereich kennzeichnen, nicht die Seite übertönen.
  entwicklung: {
    flaeche: orange[50],
    streifen: orange[100],
    kante: orange[200],
    text: orange[700],
  },

  // ABWÄRTSKOMPATIBILITÄT (DEPRECATED)
  // Diese Aliase ermöglichen die schrittweise Migration von der alten Struktur.
  // Sie werden in zukünftigen Versionen entfernt.

  // Alte primary/secondary Struktur → Nutze colors.brand stattdessen
  primary: {
    main: teal[500],
    light: teal[100],
    dark: teal[900],
    gradient: `linear-gradient(45deg, ${blue[500]}, ${blue[300]})`,
    gradientHover: `linear-gradient(45deg, ${blue[600]}, ${blue[500]})`,
  },
  secondary: {
    main: red[700],
    light: red[200],
    dark: red[700],
  },

  // Alte accent Struktur → Nutze colors.brand.primary stattdessen
  accent: {
    teal: teal[500],
  },

  // Alte utility Struktur (erweitert) → Nutze colors.border, colors.utility stattdessen
  utility: {
    white: gray[0],
    black: gray[1000],
    transparent: "transparent",
    loading: gray[100],
    darkBlue: blue[800],
    // Deprecated Aliase für alte Struktur
    lightGray: gray[100],
    mediumGray: gray[150],
    darkGray: gray[300],
    darkerGray: gray[400],
    border: gray[200],
  },

  // Alte opacity Struktur → Nutze colors.state stattdessen
  opacity: {
    primary08: alpha.blue04,
    black04: gray[50],
    black10: gray[100],
  },
} as const;

// Typ-Export für TypeScript-Unterstützung
export type Colors = typeof colors;
